import { produce } from 'immer';

import { StoreHelper } from '@helpers';

export interface CookieMetadata {
	name?: string;
	version?: string;
	expirationTime?: number;
}

export interface CookiePrivacyPreference {
	idFamily?: string;
	familyName?: string;
	familyDescription?: string;
	isRequired?: boolean;
	accepted?: boolean;
}

export interface CookiePrivacy {
	metadata?: CookieMetadata;
	preferences?: CookiePrivacyPreference[];
}

export interface CookieRemarketing {
	metadata?: CookieMetadata;
	accepted?: boolean;
}

export interface CookieBrowserInfo {
	metadata?: CookieMetadata;
	value?: string;
}

export interface CookiesPreferencesState {
	privacy: CookiePrivacy;
	remarketing: CookieRemarketing;
}

export const initialCookiesPreferencesState: CookiesPreferencesState = {
	privacy: {},
	remarketing: {
		accepted: false,
	},
};

interface UseCookiesPreferencesStoreOutput extends CookiesPreferencesState {
	setPrivacyCookie: (privacy: CookiePrivacy) => void;
	togglePrivacyAccepted: (idFamily: string) => void;
	setPrivacyPreferences: (preferences: CookiePrivacyPreference[]) => void;
	setRemarketingCookie: (remarketing: CookieRemarketing) => void;
	setRemarketingAccepted: (accepted: boolean) => void;
}

export const useCookiesPreferencesStore = StoreHelper.createStore<UseCookiesPreferencesStoreOutput>(
	(set) => ({
		...initialCookiesPreferencesState,
		setPrivacyCookie: function (privacy: CookiePrivacy) {
			set(
				produce((currentState: CookiesPreferencesState) => ({
					...currentState,
					privacy,
				})),
				false,
				'setPrivacyCookie'
			);
		},
		togglePrivacyAccepted: function (idFamily: string) {
			set(
				produce((currentState: CookiesPreferencesState) => {
					const currentPreferences = currentState.privacy.preferences;
					const newPreferences = currentPreferences?.map((pref) => ({
						...pref,
						accepted: pref.idFamily === idFamily ? !pref.accepted : pref.accepted,
					}));

					return {
						...currentState,
						privacy: {
							...currentState.privacy,
							preferences: newPreferences,
						},
					};
				}),
				false,
				'togglePrivacyAccepted'
			);
		},
		setPrivacyPreferences: function (preferences: CookiePrivacyPreference[]) {
			set(
				produce((currentState: CookiesPreferencesState) => ({
					...currentState,
					privacy: {
						...currentState.privacy,
						preferences,
					},
				})),
				false,
				'setPrivacyPreferences'
			);
		},
		setRemarketingCookie: function (remarketing: CookieRemarketing) {
			set(
				produce((currentState: CookiesPreferencesState) => ({
					...currentState,
					remarketing,
				})),
				false,
				'setRemarketingCookie'
			);
		},
		setRemarketingAccepted: function (accepted: boolean) {
			set(
				produce((currentState: CookiesPreferencesState) => ({
					...currentState,
					remarketing: {
						...currentState.remarketing,
						accepted,
					},
				})),
				false,
				'setRemarketingAccepted'
			);
		},
	}),
	{ name: 'CookiesPreferences' }
);
